import {omneticApi} from '@dms/api/base';

import {
  AddTenantEnumOptionApiArg,
  AddTenantEnumOptionApiResponse,
  CustomFieldsPayloadApiArg,
  CustomFieldsPayloadApiResponse,
  CustomFieldsSchemaApiArg,
  CustomFieldsSchemaApiResponse,
  FieldDefinitionListParams,
  RecordPayloadPatchApiArg,
  RecordPayloadPatchApiResponse,
  RemoveTenantEnumOptionApiArg,
  RemoveTenantEnumOptionApiResponse,
  RenameTenantEnumOptionApiArg,
  RenameTenantEnumOptionApiResponse,
  ReorderFieldDefinitionsApiArg,
  ReorderFieldDefinitionsApiResponse,
  SetTenantEnumPriorityApiArg,
  SetTenantEnumPriorityApiResponse,
  TenantFieldDefinitionAddParams,
  TenantFieldDefinitionRemoveParams,
  TenantFieldDefinitionResponseBody,
  TenantFieldDefinitionUpdateParams,
  ToggleTenantEnumOptionApiArg,
  ToggleTenantEnumOptionApiResponse,
  UpdateCustomFieldsPayloadApiArg,
  UpdateCustomFieldsPayloadApiResponse,
} from './types';

export const customFieldsApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    addFieldDefinition: build.mutation<
      TenantFieldDefinitionResponseBody,
      TenantFieldDefinitionAddParams
    >({
      query: ({body}) => ({
        url: `/dms/v1/custom-fields/definition/tenant`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['customFieldList', 'entityCustomFields'],
    }),
    deleteFieldDefinition: build.mutation<void, TenantFieldDefinitionRemoveParams>({
      query: ({resourceId, definitionId}) => ({
        url: `/dms/v1/custom-fields/definition/tenant/${resourceId}/${definitionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['customFieldList', 'entityCustomFields'],
    }),
    updateFieldDefinition: build.mutation<
      TenantFieldDefinitionResponseBody,
      TenantFieldDefinitionUpdateParams
    >({
      query: ({resourceId, definitionId, body}) => ({
        url: `/dms/v1/custom-fields/definition/tenant/${resourceId}/${definitionId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['customFieldList', 'entityCustomFields'],
    }),
    getFieldDefinitionList: build.query<
      TenantFieldDefinitionResponseBody[],
      FieldDefinitionListParams
    >({
      query: ({type, resourceId}) => ({
        url: `/dms/v1/custom-fields/definition/all`,
        method: 'GET',
        params: {type, resourceId},
      }),
      providesTags: ['customFieldList'],
    }),
    reorderFieldDefinitions: build.mutation<
      ReorderFieldDefinitionsApiResponse,
      ReorderFieldDefinitionsApiArg
    >({
      query: ({resourceId, ...body}) => ({
        url: `/dms/v1/custom-fields/definition/resource/${resourceId}/order`,
        method: 'PUT',
        body,
      }),
      onQueryStarted({resourceId, items}, {dispatch, queryFulfilled}) {
        const patchResult = dispatch(
          customFieldsApi.util.updateQueryData(
            'getFieldDefinitionList',
            {type: 'tenant'},
            (draft) => {
              const otherDefinitions = draft.filter((def) => def.resource.id !== resourceId);

              const reorderedResourceDefinitions = items
                .map((id) => draft.find((def) => def.id === id))
                .filter((def) => def !== undefined);

              return [...otherDefinitions, ...reorderedResourceDefinitions];
            }
          )
        );

        queryFulfilled.catch(() => {
          patchResult.undo();
        });
      },
    }),
    getCustomFieldsSchema: build.query<CustomFieldsSchemaApiResponse, CustomFieldsSchemaApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/model-schema/custom-fields`,
        params: {
          recordId: queryArg.recordId,
          resourceId: queryArg.resourceId,
        },
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'entityCustomFields', id: queryArg.recordId},
      ],
    }),
    getCustomFieldsPayload: build.query<CustomFieldsPayloadApiResponse, CustomFieldsPayloadApiArg>({
      query: ({recordId, resourceId}) => ({
        url: `/dms/v1/custom-fields/payload/${resourceId}/${recordId}`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'entityCustomFields', id: queryArg.recordId},
      ],
    }),
    updateCustomFieldsPayload: build.mutation<
      UpdateCustomFieldsPayloadApiResponse,
      UpdateCustomFieldsPayloadApiArg
    >({
      query: ({recordId, resourceId, fieldValues}) => ({
        url: `/dms/v1/custom-fields/payload/${resourceId}/${recordId}`,
        method: 'POST',
        body: fieldValues,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'entityCustomFields', id: queryArg.recordId},
      ],
    }),
    patchCustomFieldsPayload: build.mutation<
      RecordPayloadPatchApiResponse,
      RecordPayloadPatchApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/custom-fields/payload/${queryArg.resourceId}/${queryArg.recordId}`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'entityCustomFields', id: queryArg.recordId},
      ],
    }),
    addTenantEnumOption: build.mutation<AddTenantEnumOptionApiResponse, AddTenantEnumOptionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/custom-fields/definition/tenant/${queryArg.resourceId}/${queryArg.definitionId}/enum`,
        method: 'POST',
        body: queryArg.addEnumOptionRequestBody,
      }),
      invalidatesTags: ['customFieldList'],
    }),
    removeTenantEnumOption: build.mutation<
      RemoveTenantEnumOptionApiResponse,
      RemoveTenantEnumOptionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/custom-fields/definition/tenant/${queryArg.resourceId}/${queryArg.definitionId}/enum/${queryArg.enumOptionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['customFieldList'],
    }),
    renameTenantEnumOption: build.mutation<
      RenameTenantEnumOptionApiResponse,
      RenameTenantEnumOptionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/custom-fields/definition/tenant/${queryArg.resourceId}/${queryArg.definitionId}/enum/${queryArg.enumOptionId}/name`,
        method: 'PUT',
        body: queryArg.renameEnumOptionRequestBody,
      }),
      invalidatesTags: ['customFieldList'],
    }),
    toggleTenantEnumOption: build.mutation<
      ToggleTenantEnumOptionApiResponse,
      ToggleTenantEnumOptionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/custom-fields/definition/tenant/${queryArg.resourceId}/${queryArg.definitionId}/enum/${queryArg.enumOptionId}/active`,
        method: 'PUT',
        body: queryArg.toggleEnumOptionRequestBody,
      }),
      invalidatesTags: ['customFieldList'],
    }),
    setTenantEnumPriority: build.mutation<
      SetTenantEnumPriorityApiResponse,
      SetTenantEnumPriorityApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/custom-fields/definition/tenant/${queryArg.resourceId}/${queryArg.definitionId}/enum-priority`,
        method: 'PUT',
        body: queryArg.setEnumPriorityRequestBody,
      }),
      onQueryStarted(
        {resourceId, definitionId, setEnumPriorityRequestBody},
        {dispatch, queryFulfilled}
      ) {
        const patchResult = dispatch(
          customFieldsApi.util.updateQueryData(
            'getFieldDefinitionList',
            {type: 'tenant', resourceId},
            (draft) => {
              const definition = draft.find((def) => def.id === definitionId);

              if (!definition) {
                return;
              }

              const reorderedOptions = setEnumPriorityRequestBody.options
                .map((option) => {
                  const existing = definition.enumOptions.find(
                    (enumOption) => enumOption.uuid === option.uuid
                  );

                  if (!existing) {
                    return undefined;
                  }

                  return {...existing, priority: option.priority};
                })
                .filter((option) => option !== undefined);

              definition.enumOptions = reorderedOptions;
            }
          )
        );

        queryFulfilled.catch(() => {
          patchResult.undo();
        });
      },
      invalidatesTags: ['customFieldList'],
    }),
  }),
});

export const {
  useRemoveTenantEnumOptionMutation,
  useRenameTenantEnumOptionMutation,
  useToggleTenantEnumOptionMutation,
  useSetTenantEnumPriorityMutation,
  useAddTenantEnumOptionMutation,
  useReorderFieldDefinitionsMutation,
  useUpdateCustomFieldsPayloadMutation,
  useGetCustomFieldsPayloadQuery,
  useLazyGetCustomFieldsPayloadQuery,
  useGetCustomFieldsSchemaQuery,
  useAddFieldDefinitionMutation,
  useDeleteFieldDefinitionMutation,
  useUpdateFieldDefinitionMutation,
  useGetFieldDefinitionListQuery,
  useLazyGetFieldDefinitionListQuery,
  usePatchCustomFieldsPayloadMutation,
} = customFieldsApi;
